import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Star, X, BookOpen, Calendar as CalendarIcon, Loader2, Heart } from 'lucide-react';
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { getAuthToken } from '@/utils/auth';
import { toast } from 'sonner';
import { usePreferences } from '@/context/PreferencesContext';
import { cn } from '@/lib/utils';

export default function TeacherRatingModal() {
  const { preferences, toggleFavoriteTutor } = usePreferences() || {};
  const [isOpen, setIsOpen] = useState(false);
  const [booking, setBooking] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ratingForm, setRatingForm] = useState({
    teaching_style: 5,
    engagement: 5,
    simplification: 5,
    friendliness: 5,
    comment: ''
  });

  const openRatingForBooking = useCallback(async (targetBooking, bookingId) => {
    const bId = bookingId || targetBooking?.id;
    if (!bId) return;

    // Check if already rated or dismissed
    try {
      const rated = JSON.parse(localStorage.getItem('rated_tutor_bookings') || '[]');
      if (rated.includes(bId) || rated.includes(Number(bId)) || rated.includes(String(bId))) {
        console.log('TeacherRatingModal: Booking already rated:', bId);
        return;
      }
    } catch (e) {
      console.error(e);
    }

    let resolvedBooking = targetBooking;

    // If targetBooking lacks basic details (like tutor name or subject), try fetching from API
    if (!resolvedBooking?.tutor_name && !resolvedBooking?.tutorName) {
      try {
        const token = getAuthToken('student') || getAuthToken();
        if (token) {
          const res = await axios.get(apiUrl(`superadmin/tutor-bookings/${bId}/`), {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res?.data) {
            resolvedBooking = { ...res.data, ...(targetBooking || {}) };
          }
        }
      } catch (err) {
        console.warn('Could not fetch full booking details for rating modal:', err);
      }
    }

    setBooking(resolvedBooking || { id: bId });
    setRatingForm({
      teaching_style: 5,
      engagement: 5,
      simplification: 5,
      friendliness: 5,
      comment: ''
    });
    setIsOpen(true);
  }, []);

  useEffect(() => {
    const handleOpenEvent = (e) => {
      const { bookingId, booking: eventBooking } = e.detail || {};
      if (eventBooking || bookingId) {
        openRatingForBooking(eventBooking, bookingId);
      }
    };

    window.addEventListener('open_tutor_rating', handleOpenEvent);
    return () => window.removeEventListener('open_tutor_rating', handleOpenEvent);
  }, [openRatingForBooking]);

  const handleClose = (open) => {
    setIsOpen(open);
    if (!open && booking?.id) {
      try {
        const dismissed = JSON.parse(localStorage.getItem('dismissed_ratings_bookings') || '[]');
        if (!dismissed.includes(booking.id)) {
          dismissed.push(booking.id);
          localStorage.setItem('dismissed_ratings_bookings', JSON.stringify(dismissed));
        }
      } catch (e) {
        console.error('Failed to save dismissed ratings', e);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!booking?.id) return;

    setSubmitting(true);
    try {
      const token = getAuthToken('student') || getAuthToken();
      await axios.post(apiUrl('superadmin/tutor-feedbacks/'), {
        booking: booking.id,
        ...ratingForm
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      try {
        const rated = JSON.parse(localStorage.getItem('rated_tutor_bookings') || '[]');
        if (!rated.includes(booking.id)) {
          rated.push(booking.id);
          localStorage.setItem('rated_tutor_bookings', JSON.stringify(rated));
        }
      } catch (err) {
        console.error(err);
      }

      toast.success('Thank you! Feedback recorded successfully.');
      setIsOpen(false);
      setBooking(null);

      // Dispatch event to inform other components (like TuitionsPage) to refresh
      window.dispatchEvent(new CustomEvent('tutor_rating_submitted', { detail: { bookingId: booking.id } }));
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Failed to submit ratings.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !booking) return null;

  const tutorName = booking.tutor_name || booking.tutorName || 'Your Tutor';
  const tutorAvatar = booking.tutor_avatar_url || booking.tutorAvatarUrl || `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(tutorName)}`;
  const subjectName = booking.subject_name || booking.subjectName || '1-on-1 Class';
  const chapterName = booking.chapter_name || booking.chapterName;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        showCloseButton={false}
        className="w-[calc(100vw-1.5rem)] sm:w-full max-w-lg max-h-[88vh] bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground rounded-2xl p-0 overflow-hidden flex flex-col my-auto !p-0 shadow-2xl z-[9999]"
      >
        {/* Header banner */}
        <div className="bg-[hsl(var(--main-bg))] border-b border-[hsl(var(--card-border))] px-4 py-3 sm:px-6 sm:py-4 relative overflow-hidden shrink-0">
          <div className="relative flex items-center justify-between gap-2.5">
            <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                <img
                  src={tutorAvatar}
                  alt={tutorName}
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg object-cover"
                />
              </div>
              <div className="min-w-0">
                <DialogHeader className="p-0 text-left">
                  <span className="text-muted-foreground text-[10px] font-bold uppercase tracking-widest block mb-0.5">Rate Your Session</span>
                  <div className="flex items-center gap-2">
                    <DialogTitle className="text-foreground text-sm sm:text-base font-bold leading-tight truncate">
                      {tutorName}
                    </DialogTitle>
                    {booking?.tutor && toggleFavoriteTutor && (() => {
                      const tutorId = booking.tutor;
                      const isFav = Array.isArray(preferences?.favorite_tutors) &&
                        preferences.favorite_tutors.some(id => String(id) === String(tutorId));
                      return (
                        <button
                          type="button"
                          onClick={() => toggleFavoriteTutor(tutorId)}
                          className="p-1 rounded-full hover:bg-[hsl(var(--card-bg))] transition-transform active:scale-125"
                          title={isFav ? "Remove from favorite tutors" : "Add to favorite tutors"}
                        >
                          <Heart
                            className={cn(
                              "w-4 h-4 transition-colors",
                              isFav ? "text-rose-500 fill-rose-500" : "text-muted-foreground hover:text-rose-400"
                            )}
                          />
                        </button>
                      );
                    })()}
                  </div>
                  <DialogDescription className="sr-only">
                    Provide feedback for tutor {tutorName}.
                  </DialogDescription>
                </DialogHeader>
              </div>
            </div>

            {/* Header Close Button */}
            <button
              type="button"
              onClick={() => handleClose(false)}
              className="w-8 h-8 rounded-full bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] text-muted-foreground hover:text-foreground flex items-center justify-center transition shrink-0"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Session detail pill */}
          <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
            <span className="inline-flex items-center gap-1 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] px-2 py-0.5 rounded-full font-semibold">
              <BookOpen className="w-2.5 h-2.5 text-emerald-500" /> {subjectName}
            </span>
            {booking.booking_date && (
              <span className="inline-flex items-center gap-1 bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] px-2 py-0.5 rounded-full font-semibold">
                <CalendarIcon className="w-2.5 h-2.5 text-emerald-500" />
                {new Date(`${booking.booking_date}T${booking.start_time || '00:00:00'}Z`)
                  .toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                {booking.start_time && (
                  <>
                    &nbsp;•&nbsp;
                    {new Date(`${booking.booking_date}T${booking.start_time}Z`)
                      .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </>
                )}
              </span>
            )}
            {chapterName && (
              <span className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full font-medium truncate max-w-[180px]">
                {chapterName}
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-3.5 py-3 sm:px-6 sm:py-4 space-y-2.5 sm:space-y-3.5 overflow-y-auto flex-1 min-h-0 overscroll-contain">
          {/* Metric rows */}
          {[
            { key: 'teaching_style', label: 'Teaching Style', desc: 'Clarity and structure' },
            { key: 'engagement', label: 'Engagement', desc: 'Active student interaction' },
            { key: 'simplification', label: 'Simplification', desc: 'Breaking down concepts' },
            { key: 'friendliness', label: 'Friendliness', desc: 'Patience and attitude' },
          ].map(c => (
            <div key={c.key} className="flex items-center justify-between gap-2 p-2 sm:p-2.5 rounded-xl bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))]">
              <div className="min-w-0 pr-1">
                <p className="text-xs font-bold text-foreground truncate">{c.label}</p>
                <p className="text-[10px] text-muted-foreground truncate">{c.desc}</p>
              </div>
              <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
                {[1, 2, 3, 4, 5].map(val => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setRatingForm({ ...ratingForm, [c.key]: val })}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center transition-all ${ratingForm[c.key] >= val
                      ? 'text-amber-400 scale-105'
                      : 'text-muted-foreground/40 hover:text-amber-300'
                      }`}
                    title={`${val} star${val > 1 ? 's' : ''}`}
                  >
                    <Star className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${ratingForm[c.key] >= val ? 'fill-current' : ''}`} />
                  </button>
                ))}
                <span className="text-[11px] font-bold text-amber-500 w-5 text-center ml-0.5">{ratingForm[c.key]}</span>
              </div>
            </div>
          ))}

          {/* Comment */}
          <div className="space-y-1 pt-1">
            <Label htmlFor="global_rating_comment_input" className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Your Comments (Optional)</Label>
            <textarea
              id="global_rating_comment_input"
              rows={2}
              placeholder="Share how the session went or any feedback..."
              value={ratingForm.comment}
              onChange={e => setRatingForm({ ...ratingForm, comment: e.target.value })}
              className="w-full bg-[hsl(var(--input-bg))] text-foreground rounded-xl border border-[hsl(var(--card-border))] p-2 sm:p-2.5 text-xs focus:ring-1 focus:ring-emerald-500 focus:outline-none resize-none placeholder:text-[10px] sm:placeholder:text-xs placeholder:text-muted-foreground/50"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[hsl(var(--card-border))] shrink-0">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleClose(false)}
              className="text-muted-foreground hover:bg-[hsl(var(--sidebar-hover))] text-xs sm:text-sm h-8 sm:h-9"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-bold text-xs sm:text-sm px-4 sm:px-5 h-8 sm:h-9 shadow-md shadow-emerald-600/20 border-0"
            >
              {submitting ? (
                <><Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 animate-spin" />Submitting...</>
              ) : (
                <><Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 fill-current" />Submit Feedback</>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
